import { mapActions, mapGetters } from 'vuex'
import { entitySlugForLocale, computeAlternateUrls } from '@/utils/hreflang'
import {
  productSharedBulbs,
  sharedBulbQuestion,
  sharedBulbSpecification,
} from '@/utils/sharedBulbCart'

export default {
  data() {
    return {
      associated_products: [],
      associated_articles: [],
      motorcycle_base_product: null,
      productLoaded: null,
      manualFilterUpdate: false,
      languageMap: {},
    }
  },

  computed: {
    ...mapGetters({
      product_store: 'product',
      filters_product_selected_store: 'filters_product_selected',
    }),
    product: {
      get() {
        return this.product_store ? this.product_store : null
      },
      set(value) {
        this.setProduct(value)
      },
    },
    filters_product_selected: {
      get() {
        return this.filters_product_selected_store
          ? this.filters_product_selected_store
          : null
      },
      set(value) {
        this.setFiltersProductSelected(value)
      },
    },
  },
  methods: {
    ...mapActions({
      setProduct: 'setProduct',
      setFiltersProductSelected: 'setFiltersProductSelected',
    }),

    async loadLanguageMap() {
      this.languageMap = await this.$getLanguageMap()
    },

    async getProduct() {
      if (
        this.product &&
        this.product.slug !== this.$route.params.slug &&
        !this.$route.query.fv
      ) {
        this.setFiltersProductSelected({})
        this.$store.dispatch('setLastFilterSelected', null)
      }

      const filtersSelected = Object.values({
        ...this.filters_product_selected,
      })
      const params = {
        current_filter_value_product_id: this.$route.query.fv
          ? undefined
          : this.$store.state.last_filter_selected,
        filter_value_products: filtersSelected.length
          ? filtersSelected.join(',')
          : undefined,
        variant_id:
          this.$route.query.fv ||
          (this.$route.query.variant && !filtersSelected.length)
            ? this.$route.query.variant
            : undefined,
      }

      await this.fetchProductWithRetry(this.$route.params.slug, params)
        .then(({ data }) => {
          if (data) {
            if (this.$route.params.slug !== data.slug) {
              const correctPath = this.localePath({
                name: 'produit-slug',
                params: { slug: data.slug },
              })
              if (process.server) {
                this.$nuxt.context.redirect(301, correctPath)
              } else {
                this.$router.replace(correctPath)
              }
              return
            }

            const productData = data
            this.productLoaded = productData
            this.mainImg = productData.variant.images[0]

            let defaultFilters = {}
            this.setFiltersProductSelected(defaultFilters)

            if (productData.variant.filter_value_products?.length > 0) {
              this.$router.push(
                this.localePath({
                  query: {
                    variant: productData.variant.id,
                  },
                })
              )

              productData.variant.filter_value_products.forEach(item => {
                defaultFilters[item.filter_value.filter_id] = item.id
              })
            }

            if (productData.headlight_types?.length > 0) {
              const headlightTypesSpecification = {
                specification: {
                  name:
                    productData.headlight_types.length > 1
                      ? this.$t('mixins.productMixin.compatibleHeadlightTypes')
                      : this.$t('mixins.productMixin.compatibleHeadlightType'),
                },
                value: productData.headlight_types.map(item => item.name),
                sliceLinkContent: true,
              }
              productData.variant.specification_values?.unshift(
                headlightTypesSpecification
              )
              productData.variant.specification_values_featured?.unshift(
                headlightTypesSpecification
              )
            }

            const sharedBulbs = productSharedBulbs(productData.shared_bulbs)
            if (sharedBulbs.length > 0) {
              const dualFunctionSpecifications = sharedBulbs.map(sharedBulb =>
                sharedBulbSpecification(
                  sharedBulb,
                  this.$t('mixins.productMixin.dualFunction')
                )
              )
              const dualFunctionIndex =
                productData.headlight_types?.length > 0 ? 1 : 0
              productData.variant.specification_values?.splice(
                dualFunctionIndex,
                0,
                ...dualFunctionSpecifications
              )
              productData.variant.specification_values_featured?.splice(
                dualFunctionIndex,
                0,
                ...dualFunctionSpecifications
              )
              productData.questions = [
                ...(productData.questions || []),
                ...sharedBulbs.map(sharedBulb =>
                  sharedBulbQuestion(sharedBulb, (key, params) =>
                    this.$t(key, params)
                  )
                ),
              ]
            }

            if (
              !productData.hide_quantity_details &&
              Array.isArray(productData.variant.variants_items) &&
              productData.variant.variants_items.length > 0
            ) {
              const labels = [
                ...new Set(
                  productData.variant.variants_items.map(
                    item => item.pivot.label
                  )
                ),
              ]
              const specContent = labels
                .map(label => {
                  const items = productData.variant.variants_items
                    .filter(item => item.pivot.label === label)
                    .map(item => {
                      let name
                      if (item.pivot.quantity > 1 && item.name_plural) {
                        name = item.name_plural
                      } else if (
                        item.pivot.quantity === 1 &&
                        item.name_singular
                      ) {
                        name = item.name_singular
                      } else if (item.pivot.label) {
                        name = item.pivot.label
                      } else if (item.variant_generic_type) {
                        name =
                          item.pivot.quantity > 1
                            ? item.variant_generic_type.name_plural
                            : item.variant_generic_type.name_singular
                      } else if (item.name) {
                        name = item.name
                      } else {
                        return null
                      }
                      return `(${item.pivot.quantity}x) ${name}`
                    })
                    .filter(Boolean)
                    .join(', ')
                  return items
                })
                .join(', ')
              const totalPiecesSpecification = {
                specification: {
                  name: this.$t('mixins.productMixin.quantityDetails'),
                },
                value: specContent,
              }
              productData.variant.specification_values =
                productData.variant.specification_values || []
              productData.variant.specification_values.push(
                totalPiecesSpecification
              )
            } else if (!productData.hide_quantity_details) {
              const productContent = {
                specification: {
                  name: this.$t('mixins.productMixin.quantityDetails'),
                },
                value: `(1x) ${
                  productData.variant.name_alt ||
                  productData.variant.google ||
                  productData.variant.name
                }`,
              }
              productData.variant.specification_values =
                productData.variant.specification_values || []
              productData.variant.specification_values.push(productContent)
            }
            this.setProduct(productData)
          } else {
            this.$410Error()
          }
        })
        .catch(error => {
          const status = error?.response?.status
          if (status === 404) {
            this.$404Error()
          } else if (status === 410) {
            this.$410Error()
          } else if (status === 451) {
            this.$451Error()
          } else {
            console.error('Product fetch error:', status || error?.message)
            this.$nuxt.context.error({ statusCode: 503 })
          }
        })
    },

    async fetchProductWithRetry(slug, params, attempts = 2) {
      let lastError
      for (let attempt = 0; attempt < attempts; attempt++) {
        try {
          return await this.$productService.fetchProductDetail(slug, params)
        } catch (error) {
          lastError = error
          const status = error?.response?.status
          if (status === 404 || status === 410 || status === 451) {
            throw error
          }
          if (attempt < attempts - 1) {
            await new Promise(resolve => setTimeout(resolve, 300))
          }
        }
      }
      throw lastError
    },

    getArticles() {
      return this.$productService
        .fetchProductDetailRelatedArticles(this.productLoaded.slug)
        .then(({ data }) => {
          this.associated_articles = data
        })
        .catch(() => {
          this.associated_articles = []
        })
    },

    getMotorcycleBaseProduct() {
      return this.$productService
        .fetchProductDetailMotorcycleBase(this.productLoaded.slug)
        .then(({ data }) => {
          if (data) {
            data.label_text = this.$t(
              'pages.produit.slug.alsoAvailableForMotorcycle.badge'
            )
            data.label_color = '#1a1a1a'
            this.motorcycle_base_product = data
          } else {
            this.motorcycle_base_product = null
          }
        })
        .catch(() => {
          this.motorcycle_base_product = null
        })
    },

    getAssociatedProducts() {
      return this.$productService
        .fetchProductDetailAssociatedProducts(this.productLoaded.slug)
        .then(({ data }) => {
          this.associated_products = data
        })
        .catch(() => {
          this.associated_products = []
        })
    },
  },

  watch: {
    productLoaded: function (newVal, oldVal) {
      if ((newVal && !oldVal) || (newVal && oldVal && newVal.id != oldVal.id)) {
        if (process.client && oldVal && !this.$fetchState.pending) {
          this.getAssociatedProducts()
          this.getMotorcycleBaseProduct()
          this.getArticles()
        }

        if (process.client && newVal.variant) {
          try {
            const eventId = this.$trackViewItem(newVal, newVal.variant)
            if (eventId) {
              this.$metaConversionService
                .viewContent({ product_id: newVal.id, event_id: eventId })
                .catch(() => {})
            }
          } catch (e) {}
        }

        if (process.client && this.$auth?.loggedIn) {
          this.$visitService
            .create({
              visitable_id: newVal.id,
              visitable_type: 'product',
            })
            .catch(() => {})
        }
      }
    },
  },

  async fetch() {
    await Promise.all([this.getProduct(), this.loadLanguageMap()])

    if (this.productLoaded?.slug) {
      await Promise.all([
        this.getAssociatedProducts(),
        this.getMotorcycleBaseProduct(),
        this.getArticles(),
      ])
    }

    this.$store.commit(
      'SET_RESTRICTED_LOCALES',
      this.product?.restricted_locales || []
    )

    if (this.product?.slug && this.$route.params.slug === this.product.slug) {
      computeAlternateUrls({
        locales: this.$i18n.locales,
        localePath: this.localePath,
        routeName: 'produit-slug',
        buildParams: code => {
          if ((this.product.restricted_locales || []).includes(code))
            return null
          const slug = entitySlugForLocale(this, this.product, code)
          return slug ? { slug } : null
        },
        store: this.$store,
      })
    }
  },
}
